import React from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import Button from '@tencent/dui/lib/components/Button';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import styles from './index.less';
import { reaction } from 'mobx';
import { IThemeColorManager } from '@src/slide/application/service/theme-color-manager/index.interface';
import i18n from '@i18next/i18n';
import { ThemePreviewList } from './theme-preview-list';
import { ThemeSelectList } from './theme-select-list';
import { IReactionDisposer } from 'mobx/lib/internal';
import {
    IThemeSelectItem,
} from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { IRightMenuManager } from '@src/slide/application/service/right-menu-manager/core/right-menu-manager.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { PPTEditorStateEnumType } from '../../edit-extension/edit-extension-export';
import { ITipsManager } from '../../tips-manager/core/tips-manager.interface';
import { IUserPriviledgeService } from '../../user-priviledge-service/user-priviledge-service.interface';

interface IThemeColorDialogState {
    themeColorManager?: IThemeColorManager;
    selectedScaleList: ISelectedScaleList;
    currentSelectedTheme?: IThemeSelectItem;
    canUndo: boolean;
    selectedKey: string;
}

interface ISelectedScaleList {
    [selectedIndex: number]: boolean;
}

export interface IThemeColorDialogProps {
    themeColorManager?: IThemeColorManager;
    menuManager?: IMenuManager;
    uiDataService: IUiDataService;
    rightMenuManager?: IDynamicService<IRightMenuManager>;
    slideWidth: number;
    slideHeight: number;
    tipsManager: ITipsManager;
    userPrivilegeService: IUserPriviledgeService;
}

/**
 * 一键换色主题弹窗
 */
export default class ThemeColorDialog extends React.Component<IThemeColorDialogProps, IThemeColorDialogState> {
    private reactionDispose: IReactionDisposer;
    private isSnackbarClose = true;
    private loadingBar;
    constructor(props) {
        super(props);
        this.state = {
            selectedScaleList: [],
            canUndo: false,
            selectedKey: '',
        };
        this.initEvent();
    }

    public componentDidMount() {
    }

    public componentWillUnmount() {
        this.reactionDispose?.();
    }

    render() {
        const { slideWidth, slideHeight } = this.props;
        const cloneUiDataService = this.getCloneUiDataService();
        cloneUiDataService?.presentation?.children?.forEach((children) => {
            children.startConvertChildrenTask();
        });
        return <Modal
            zIndex={9999}
            destroyOnClose={true}
            className={styles['theme-modal-container']}
            title={<ModalTitle
                title={i18n.t('一键换色')}
            />}
            onCancel={this.onCancel}
            footer={<ModalFooter
                disabledOk={!this.state.selectedKey}
                onOk={() => {
                    this.handleOk();
                }}
                onCancel={this.onCancel}
            />}
            style={{
                padding: 0,
            }}
            visible={this.props.themeColorManager?.themeColorDialogVisible}>
            <div
                onContextMenu={(e) => {
                    e.preventDefault();
                }}
                className={styles['theme-dialog-container']}>
                <ThemePreviewList
                    slideWidth={slideWidth}
                    slideHeight={slideHeight}
                    canRevert={!!this.state.selectedKey}
                    cloneUiDataService={cloneUiDataService}
                    onSelect={() => {}}
                />
                <ThemeSelectList
                    themeColorManager={this.props.themeColorManager}
                    rightMenuManager={this.props.rightMenuManager}
                    menuManager={this.props.menuManager}
                    selectedKey={this.state.selectedKey}
                    handleThemeSelect={this.handleThemeSelect}
                />
            </div>
        </Modal>;
    }

    private onCancel = () => {
        this.closeModal();
        this.setState({
            selectedKey: '',
            currentSelectedTheme: undefined,
        });
        this.props.themeColorManager?.onCancel?.();
    };

    private getCloneUiDataService = () => this.props.themeColorManager?.cloneUiDataService;

    private updateComponent = () => {
        this.forceUpdate();
    };

    /**
     * 保留，后续有部分切换
     * @param selectedScaleList
     */
    private onSelect = (selectedScaleList: ISelectedScaleList) => {
        this.setState({
            selectedScaleList,
        });
    };

    private closeModal = () => {
        const { themeColorManager } = this.props;
        themeColorManager?.updateThemeColorDialogVisible(false);
    };

    /**
     * 选择主题，会触发主题映射
     * @param theme
     * @param selectedKey
     */
    private handleThemeSelect = async (theme: IThemeSelectItem, selectedKey?: string) => {
        if (this.state.currentSelectedTheme === theme) return;
        if (!theme || !selectedKey) {
            this.setState({
                currentSelectedTheme: undefined,
                selectedKey: '',
            });
            return;
        }
        const cloneUiDataService = this.getCloneUiDataService();
        const { themeColorManager } = this.props;
        this.handleSnackbar();
        this.setState({
            currentSelectedTheme: theme,
            selectedKey: selectedKey || this.state.selectedKey,
        });

        await cloneUiDataService?.forceConvertSlides(cloneUiDataService.presentation.children);
        requestAnimationFrame(() => {
            transaction(async () => {
                await themeColorManager?.convertCustomColorToTheme(true);
                cloneUiDataService?.presentation?.themeList?.forEach((themeModel) => {
                    themeModel.updateTheme(theme);
                });
                this.handleSnackbar();
            });
        });
    };

    /**
     * 保存选择主题
     */
    private handleOk = async () => {
        // if (!isVip() && this.state.currentSelectedTheme?.isVip) {
        //     showModelToPay({
        //         position: QUICK_TOOL_AID_POSITION.TOOLBAR,
        //         feature: QUICK_TOOL_AID_FEATURE,
        //         apprec: 'changetheme',
        //         name: i18n.t('一键换色'),
        //     });
        //     return;
        // }

        if (this.props.userPrivilegeService.pptEditorState !== PPTEditorStateEnumType.EDITABLE) {
            this.props.tipsManager.showCannotEditTips();
            return;
        }

        this.handleSnackbar();
        const { themeColorManager } = this.props;
        const { uiDataService } = this.props;
        await uiDataService?.forceConvertSlides(uiDataService.presentation.children);

        if (this.state.currentSelectedTheme) {
            requestAnimationFrame(() => {
                transaction(() => {
                    themeColorManager?.convertCustomColorToTheme(false);
                    this.state.currentSelectedTheme?.id
                    && themeColorManager?.applyThemeColor(this.state.currentSelectedTheme.id);
                    this.handleSnackbar();
                    this.setState({
                        selectedKey: '',
                        currentSelectedTheme: undefined,
                    });
                    this.closeModal();
                });
            });
        }
        if (this.state.currentSelectedTheme?.id) {
            themeColorManager?.onSelect?.(this.state.currentSelectedTheme.id);
            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.COLOR_SWITCH,
                action: TDWActionType.THEME_SUCCESS,
                ver5: this.state.currentSelectedTheme.id,
            });
        }
    };

    private handleSnackbar = () => {
        if (!this.isSnackbarClose) {
            this.loadingBar.close();
            this.isSnackbarClose = true;
        } else {
            this.loadingBar = Snackbar.show({
                autoClose: false, // 若为true（默认），则不会返回引用
                type: 'loading',
                message: `${i18n.t('正在切换中')}...`,
            });
            this.isSnackbarClose = false;
        }
    };

    // 保留
    // private revertTheme = () => {
    //     const originThemeList = this.getAppService().getUiDataService().presentation.themeList;
    //     const cloneUiDataService = this.getCloneUiDataService();
    //     transaction(() => {
    //         originThemeList.forEach((originTheme, index) => {
    //             const cloneTheme = cloneUiDataService?.presentation?.themeList?.[index];
    //             const colorList: Array<IThemeColor> = this.getColorListFromThemeModel(originTheme);
    //             cloneTheme?.updateTheme(colorList);
    //         });
    //     });
    //     // 接口修改了，如果后续要上主题恢复，这块的接口要改
    //     this.setState({
    //         currentSelectedTheme: undefined,
    //         selectedKey: '',
    //     });
    // };

    private initEvent = () => {
        this.reactionDispose = reaction(() => [
            this.props.themeColorManager?.themeColorDialogVisible,
        ], () => {
            this.updateComponent();
        });

        // 保留，主题还原
        // this.getThemeColorManager()?.addUndoEventCallBack((canUndo: boolean) => {
        //     this.setState({
        //         canUndo,
        //     });
        // });
    };
}

interface IModalTitleProps {
    title: string;
}

export class ModalTitle extends React.Component<IModalTitleProps, any> {
    render() {
        return <div className={styles['modal-title-container']}>
            <div
                className={styles['modal-title-wrapper']}
            >
                { this.props.title }
            </div>
            {/* <img */}
            {/*     className={styles['modal-icon']} */}
            {/*     src={VIP_ICON} */}
            {/* /> */}
            {/* <div */}
            {/*     className={styles['modal-vip-tips']} */}
            {/* > */}
            {/*     { i18n.t('文档超级会员尊享所有主题颜色') } */}
            {/* </div> */}
            {/* { */}
            {/*     !isVip() && <div */}
            {/*         className={styles['modal-vip-button']} */}
            {/*         onClick={() => { */}
            {/*             ArkLoader.extensionService.getExtension(PAY_EXTENSION.name).then((pay) => { */}
            {/*                 pay.show({ */}
            {/*                 }); */}
            {/*             }); */}
            {/*         }} */}
            {/*     > */}
            {/*         { i18n.t('立即开通') } */}
            {/*     </div> */}
            {/* } */}
        </div>;
    }
}

interface IModalFooterProps {
    disabledOk: boolean;
    onOk();
    onCancel();
}

export class ModalFooter extends React.Component<IModalFooterProps, any> {
    render() {
        return <div
            className={styles['modal-footer-container']}
        >
            <div
                className={styles['modal-footer-button-container']}>
                <Button
                    onClick={this.props.onCancel}
                    style={{ marginLeft: 16 }}>
                    { i18n.t('取消') }
                </Button>
                <Button
                    disabled={this.props.disabledOk}
                    onClick={this.props.onOk}
                    type="primary" style={{ marginLeft: 16 }}>
                    { i18n.t('确定') }
                </Button>
            </div>
        </div>;
    }
}
